/**
 * Permission utilities for role-based access control
 */

// Define elevated roles that can participate in approval workflows
export const ELEVATED_ROLES = [
  'Extension Head',
  'GAD', 
  'Vice Chancellor for Administration and Finance',
  'Vice Chancellor for Research, Development and Extension Services',
  'Chancellor'
];

// Define roles that can toggle edit lock (NOT Extension Coordinator)
export const EDIT_LOCK_ROLES = [
  'GAD',
  'Extension Head',
  'Vice Chancellor for Administration and Finance',
  'Vice Chancellor for Research, Development and Extension Services',
  'Chancellor',
  'Admin'
];

// Define roles that can delete items
export const DELETE_ROLES = ['Admin'];

/**
 * Check if user can toggle edit lock for an item
 * @param {Object} user - Current user object
 * @returns {boolean} - Whether user can toggle edit lock
 */
export const canToggleEditLock = (user) => {
  if (!user) return false;
  return EDIT_LOCK_ROLES.includes(user.role);
};

/**
 * Check if user can edit an item based on edit lock status
 * When unlocked: Anyone who can see the item can edit it
 * When locked: No one can edit
 * @param {Object} user - Current user object
 * @param {Object} item - Item to check (project, workplan, report, monitoring)
 * @param {string} itemType - Type of item ('project', 'workplan', 'report', 'monitoring')
 * @returns {Object} - { canEdit: boolean, reason: string }
 */
export const canEditItem = (user, item, itemType = 'project') => {
  if (!user || !item) {
    return { canEdit: false, reason: 'User or item not found' };
  }

  // Check if edit is locked - if locked, NO ONE can edit (including Admin)
  if (item.edit_locked) {
    return { canEdit: false, reason: 'Edit permissions are locked' };
  }

  // When unlocked, anyone who can see the item can edit it
  return { canEdit: true, reason: 'Edit permissions are unlocked' };
};

/**
 * Check if user can delete an item
 * @param {Object} user - Current user object
 * @param {Object} item - Item to check
 * @returns {Object} - { canDelete: boolean, reason: string }
 */
export const canDeleteItem = (user, item) => {
  if (!user || !item) {
    return { canDelete: false, reason: 'User or item not found' };
  }

  const userRole = user.role;

  // Only Admin can delete
  if (DELETE_ROLES.includes(userRole)) {
    return { canDelete: true, reason: 'Admin has delete access' };
  }

  return { canDelete: false, reason: 'Only Admin can delete items' };
};

/**
 * Get permission summary for display
 * @param {Object} user - Current user object
 * @param {Object} item - Item to check
 * @param {string} itemType - Type of item
 * @returns {Object} - Complete permission object
 */
export const getItemPermissions = (user, item, itemType = 'project') => {
  const editPermission = canEditItem(user, item, itemType);
  const deletePermission = canDeleteItem(user, item);

  return {
    canEdit: editPermission.canEdit,
    canDelete: deletePermission.canDelete,
    editReason: editPermission.reason,
    deleteReason: deletePermission.reason,
    userRole: user?.role,
    itemStatus: item?.status,
    isOwner: item?.created_by === user?.user_id || item?.coordinator_id === user?.user_id
  };
};
